import React, { useEffect, useState } from 'react';
import { AlertTriangle, Globe, X, CheckCircle, Palette } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { getStates, editProfile } from '../../api/api';
import { toast } from 'react-hot-toast';
import { fetchMeThunk } from '../../redux/slices/authSlice';
import { toggleStreaming } from '../../redux/slices/streamingSlice';

const PreferencesTab = ({ isDarkMode, toggleTheme }) => {
  const user = useSelector((state) => state.auth.user);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState(user?.state || '');
  const [loadingStates, setLoadingStates] = useState(true);
  const [pendingState, setPendingState] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark') return 'dark';
    if (saved === 'light') return 'light';
    return 'system';
  });

  const dispatch = useDispatch();
  const isStreaming = useSelector((state) => state.streaming.enabled);

  const applyTheme = (theme) => {
    if (theme === 'light' && isDarkMode) toggleTheme();
    if (theme === 'dark' && !isDarkMode) toggleTheme();
    if (theme === 'system') {
      const prefersDark = window.matchMedia(
        '(prefers-color-scheme: dark)'
      ).matches;
      if (prefersDark !== isDarkMode) toggleTheme();
    }
  };
  useEffect(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark') setSelectedTheme('dark');
    else if (saved === 'light') setSelectedTheme('light');
    else setSelectedTheme('system');
  }, [isDarkMode]);

  useEffect(() => {
    const fetchStates = async () => {
      setLoadingStates(true);
      try {
        const res = await getStates();
        if (res?.success) setStates(res.states);
      } catch (err) {
        toast.error(err.message || 'Failed to load states');
      } finally {
        setLoadingStates(false);
      }
    };
    fetchStates();
  }, []);

  const handleStateChange = (e) => {
    const newState = e.target.value;
    if (newState !== selectedState) {
      setPendingState(newState);
      setShowModal(true);
    }
  };

  const confirmChange = async () => {
    if (!pendingState) return;
    try {
      setSaving(true);
      const res = await editProfile({
        userId: user.id,
        state: pendingState,
      });
      toast.success(res?.message || 'State updated successfully');
      setShowModal(false);
      setSelectedState(pendingState);
      setPendingState(null);
      await dispatch(fetchMeThunk());
    } catch (err) {
      toast.error(err.message || 'Failed to update state');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <div
        className={`space-y-3 text-sm ${
          isDarkMode ? 'text-gray-300' : 'text-gray-700'
        }`}
      >
        <div className="flex flex-col gap-3">
          <div
            className={`border rounded-lg p-3 shadow-sm h-full ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-200 text-gray-800'
            }`}
          >
            <h3 className="text-sm font-semibold mb-4 flex items-center">
              <span
                className={`p-1 rounded-md mr-2 flex items-center justify-center ${
                  isDarkMode
                    ? 'bg-white/10 text-white'
                    : 'bg-indigo-100 text-indigo-500'
                }`}
              >
                <Globe className="w-5 h-5" />
              </span>
              State Selection
            </h3>
            <p
              className={`text-xs leading-relaxed ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              Choose the US state for which you want to receive legal
              information and responses. This affects future chats and document
              checking.
            </p>
            <select
              value={selectedState}
              onChange={handleStateChange}
              className={`border rounded-md  mt-1 px-3  py-2 w-1/2 text-sm
    focus:outline-none focus:ring focus:border-indigo-300
    ${
      isDarkMode
        ? 'bg-gray-800 text-white border-gray-500'
        : 'bg-white text-gray-800 border-gray-300'
    }`}
            >
              {loadingStates ? (
                <option disabled>Loading states...</option>
              ) : (
                <>
                  <option value="">Select one...</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </>
              )}
            </select>

            {/* Current State */}
            <p
              className={`text-xs mt-2 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              Current state:{' '}
              <span
                className={`${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                } font-medium`}
              >
                {selectedState || 'Not selected'}
              </span>
            </p>
          </div>
          {/* Theme Selection */}
          <div
            className={`border rounded-lg p-3 shadow-sm h-full ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-200 text-gray-800'
            }`}
          >
            <h3 className="text-sm font-semibold mb-4 flex items-center">
              <span
                className={`p-1 rounded-md mr-2 flex items-center justify-center ${
                  isDarkMode
                    ? 'bg-white/10 text-white'
                    : 'bg-indigo-100 text-indigo-500'
                }`}
              >
                <Palette className="w-5 h-5" />
              </span>
              Theme
            </h3>

            <p
              className={`text-xs leading-relaxed mb-4 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              Choose your preferred color theme.
            </p>

            {/* Radio Options */}
            <div className="space-y-3 text-sm">
              {[
                { key: 'light', label: 'Light' },
                { key: 'dark', label: 'Dark' },
                { key: 'system', label: 'System default' },
              ].map((option) => (
                <label
                  key={option.key}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="theme"
                    value={option.key}
                    checked={selectedTheme === option.key}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedTheme(val);
                      localStorage.setItem(
                        'theme',
                        val === 'system' ? 'system' : val
                      );
                      applyTheme(val);
                    }}
                    className="form-radio text-indigo-600"
                  />
                  <span
                    className={`${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    } text-sm`}
                  >
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div
            className={`border rounded-lg p-3 shadow-sm h-full ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-200 text-gray-800'
            }`}
          >
            <h3 className="text-sm font-semibold mb-4">Streaming Preference</h3>
            <p
              className={`text-xs leading-relaxed mb-4 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              Toggle whether AI responses are streamed in real time or returned
              in full.
            </p>

            <div className="flex items-center gap-2">
              <span
                className={`text-[12px] ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}
              >
                Streaming
              </span>
              <div
                onClick={() => {
                  dispatch(toggleStreaming());
                  toast.success(
                    isStreaming
                      ? 'Streaming disabled. Responses will arrive complete.'
                      : 'Streaming enabled. Responses will flow in real time.'
                  );
                }}
                className={`relative flex items-center w-10 h-5 rounded-full transition-colors cursor-pointer ${
                  isStreaming ? 'bg-indigo-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow-md transform transition-transform ${
                    isStreaming ? 'translate-x-5' : ''
                  }`}
                />
              </div>
            </div>

            <p
              className={`text-xs mt-2 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              Current:{' '}
              <span
                className={`${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                } font-medium`}
              >
                {isStreaming ? 'On (real-time)' : 'Off (full response)'}
              </span>
            </p>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div
            className={`relative rounded-xl w-full max-w-md p-6 shadow-xl ${
              isDarkMode ? 'bg-gray-800' : 'bg-white'
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div
                  className={`p-1.5 rounded-full ${
                    isDarkMode
                      ? 'bg-yellow-900/40 text-yellow-300'
                      : 'bg-yellow-100 text-yellow-600'
                  }`}
                >
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <h2
                  className={`text-base font-semibold ${
                    isDarkMode ? 'text-white' : 'text-gray-800'
                  }`}
                >
                  Change State Selection
                </h2>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className={`text-sm ${
                  isDarkMode
                    ? 'text-gray-400 hover:text-white'
                    : 'text-gray-400 hover:text-gray-700'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p
              className={`text-sm mb-4 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              You are about to change your selected state from{' '}
              <strong>{selectedState || 'Not selected'}</strong> to{' '}
              <strong>{pendingState}</strong>.
            </p>
            <div
              className={`rounded-md text-xs p-4 mb-4 border-l-4 ${
                isDarkMode
                  ? 'bg-yellow-900/40 border-yellow-700 text-yellow-200'
                  : 'bg-orange-50 border-orange-400 text-orange-800'
              }`}
            >
              <p>
                <strong>Note:</strong> Changing your state will affect all
                future chats and document checking. Your existing history will
                remain associated with{' '}
                <strong>{selectedState || 'your current state'}</strong>.
              </p>
            </div>

            <p
              className={`text-sm mb-4 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Are you sure you want to continue?
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className={`px-4 py-2 rounded-md text-sm border font-medium ${
                  isDarkMode
                    ? 'border-gray-500 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={confirmChange}
                disabled={saving}
                className="px-4 py-2 rounded-md text-sm font-medium bg-brand text-white hover:bg-brand flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {!saving && <CheckCircle className="w-4 h-4" />}
                <span>{saving ? 'Saving...' : 'Confirm Change'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreferencesTab;
